import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Settings2, ShieldCheck } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

const ACTIONS = [
  { k: "view", label: "View" }, { k: "create", label: "New" }, { k: "edit", label: "Edit" },
  { k: "delete", label: "Delete" }, { k: "print", label: "Print" },
];
const ALL_ACTIONS = ACTIONS.map(a => a.k);

const leavesOf = (n) => (n.children ? n.children.flatMap(leavesOf) : [n.key]);
const flattenTree = (nodes, depth = 0) =>
  nodes.flatMap((n) => [{ ...n, depth }, ...(n.children ? flattenTree(n.children, depth + 1) : [])]);

export default function AdminUserAuthorisation() {
  const [users, setUsers] = useState([]);
  const pg = usePagination(users);
  const [sel, setSel] = useState(null);       // admin user being configured
  const [tree, setTree] = useState([]);
  const [menus, setMenus] = useState({});     // {leafKey: {view,create,edit,delete,print}}
  const [locked, setLocked] = useState(false); // SaaS Admin → permanently full access
  const [busy, setBusy] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(false);

  const load = () => api.get("/admin/admin-users")
    .then(r => setUsers(r.data))
    .catch(e => toast.error(e.response?.data?.detail || "Failed to load admin users"));
  useEffect(() => { load(); }, []);

  const openEditor = async (u) => {
    setLoadingEditor(true);
    try {
      const { data } = await api.get(`/admin/admin-user-menus/${u.id}`);
      setTree(data.tree || []);
      setMenus(data.menus || {});
      setLocked(!!data.is_saas_admin);
      setSel(data.user);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load authorisation"); }
    finally { setLoadingEditor(false); }
  };

  const setKeys = (keys, action, v) =>
    setMenus(p => { const next = { ...p }; keys.forEach(k => { next[k] = { ...(next[k] || {}), [action]: v }; }); return next; });
  const setRowAll = (keys, v) =>
    setMenus(p => { const next = { ...p }; keys.forEach(k => { next[k] = Object.fromEntries(ALL_ACTIONS.map(a => [a, v])); }); return next; });

  const rows = useMemo(() => flattenTree(tree), [tree]);
  const allLeaves = useMemo(() => tree.flatMap(leavesOf), [tree]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/admin-user-menus/${sel.id}`, { menus });
      toast.success("Authorisation saved — applies immediately");
      setSel(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  // ---------- state 1: admin user list ----------
  if (!sel) {
    return (
      <div className="p-6 space-y-6" data-testid="admin-user-auth-page">
        <div>
          <h1 className="text-3xl font-bold">Admin User Authorisation</h1>
          <p className="text-muted-foreground">Per-admin-user menu &amp; submenu access for the Platform Admin panel. Click a user to configure.</p>
        </div>
        {loadingEditor && <p className="text-xs text-muted-foreground">Loading authorisation…</p>}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Authorisation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg.pageItems.map(u => {
                  const isSaas = (u.platform_role || "super_admin") === "super_admin";
                  return (
                    <TableRow key={u.id} data-testid={`auth-user-row-${u.id}`} className={isSaas ? "" : "cursor-pointer hover:bg-secondary/40"}
                      onClick={isSaas ? undefined : () => openEditor(u)}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="font-mono text-xs">{u.email}</TableCell>
                      <TableCell><Badge variant="outline">{u.platform_role}</Badge></TableCell>
                      <TableCell><Badge variant={u.is_active !== false ? "default" : "destructive"}>{u.is_active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="text-right">
                        {isSaas
                          ? <span data-testid={`auth-locked-${u.id}`} className="inline-flex items-center gap-1 text-xs text-primary"><ShieldCheck className="w-3.5 h-3.5"/> Full Access — Locked</span>
                          : <Button size="sm" variant="ghost" data-testid={`auth-configure-${u.id}`}
                              onClick={(e) => { e.stopPropagation(); openEditor(u); }}>
                              <Settings2 className="w-3.5 h-3.5"/>
                            </Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!users.length && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No admin users</TableCell></TableRow>}
              </TableBody>
            </Table>
            <PaginationBar pg={pg}/>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- state 2: per-user menu authorisation editor ----------
  return (
    <div className="p-6 max-w-5xl space-y-4" data-testid="admin-user-auth-editor">
      <Button variant="ghost" size="sm" data-testid="auth-back" onClick={() => setSel(null)}
        className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary -ml-2">
        <ArrowLeft className="w-3 h-3 mr-1"/> Back to Admin Users
      </Button>
      <div>
        <h1 className="text-2xl font-bold">{sel.name} <span className="text-sm text-muted-foreground font-normal">({sel.email})</span></h1>
        <p className="text-muted-foreground text-sm">Platform Admin panel menu &amp; submenu authorisation · Role: <Badge variant="outline">{sel.platform_role}</Badge></p>
      </div>

      {locked && (
        <div data-testid="auth-saas-locked-banner" className="border border-primary/40 bg-primary/10 text-primary rounded-sm px-4 py-3 text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4"/> The SaaS Admin always has full, permanent access to every menu and submenu. These permissions cannot be changed.
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden" data-testid="auth-matrix">
        <div className="flex items-center justify-between gap-2 p-3 border-b border-border bg-secondary/40 flex-wrap">
          <div>
            <h3 className="text-sm uppercase tracking-wide font-bold">Platform Admin Menus</h3>
            <p className="text-xs text-muted-foreground">View unchecked = menu completely hidden from this user's navigation.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" data-testid="auth-select-all" disabled={locked} onClick={() => setRowAll(allLeaves, true)}>Select All</Button>
            <Button type="button" size="sm" variant="outline" data-testid="auth-deselect-all" disabled={locked} onClick={() => setRowAll(allLeaves, false)}>Deselect All</Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Menu / Submenu</TableHead>
              {ACTIONS.map(a => <TableHead key={a.k} className="text-center w-16">{a.label}</TableHead>)}
              <TableHead className="text-center w-16">All</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(n => {
              const keys = leavesOf(n);
              const parent = !!n.children;
              return (
                <TableRow key={n.key} data-testid={`auth-row-${n.key}`} className={parent ? "bg-secondary/30" : ""}>
                  <TableCell className={parent ? "font-bold uppercase tracking-wide text-xs" : "font-medium text-sm"}
                    style={{ paddingLeft: 12 + n.depth * 22 }}>{n.label}</TableCell>
                  {ACTIONS.map(a => {
                    const count = keys.filter(k => menus[k]?.[a.k]).length;
                    const checked = count === keys.length;
                    const ind = count > 0 && !checked;
                    return (
                      <TableCell key={a.k} className="text-center">
                        <Checkbox data-testid={`auth-perm-${n.key}-${a.k}`} disabled={locked}
                          checked={ind ? "indeterminate" : checked}
                          onCheckedChange={(v) => setKeys(keys, a.k, v === true)}/>
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center">
                    {(() => {
                      const on = keys.filter(k => ALL_ACTIONS.every(a => menus[k]?.[a])).length;
                      const checked = on === keys.length;
                      const ind = on > 0 && !checked;
                      return <Checkbox data-testid={`auth-rowall-${n.key}`} disabled={locked}
                        checked={ind ? "indeterminate" : checked}
                        onCheckedChange={(v) => setRowAll(keys, v === true)}/>;
                    })()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex gap-2">
        <Button data-testid="auth-save" onClick={save} disabled={busy || locked}>{busy ? "Saving…" : "Save"}</Button>
        <Button data-testid="auth-cancel" variant="outline" onClick={() => setSel(null)}>Cancel</Button>
      </div>
    </div>
  );
}
